/**
 * Hook to read area from cookie
 *
 * Reads the area cookie set by middleware and provides it as initial data
 * for React Query. This enables:
 * - No flash of wrong area on SSR
 * - Persistent area across client navigation
 * - Fallback to API call if cookie doesn't exist
 */

import { useEffect, useState } from 'react';
import type { AreaResponse } from '@/src/lib/api';
import { AREA_COOKIE_NAME, cookieToArea, parseAreaCookie } from '@/src/lib/cookies/area';

/**
 * Get area cookie value from browser
 * Works client-side only
 */
function getAreaCookie(): string | undefined {
  if (typeof document === 'undefined') return undefined;

  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === AREA_COOKIE_NAME) {
      return decodeURIComponent(value);
    }
  }
  return undefined;
}

/**
 * Hook to read area from cookie
 * Returns partial AreaResponse if cookie exists and is valid
 */
export function useAreaFromCookie(): Partial<AreaResponse> | null {
  const [areaFromCookie, setAreaFromCookie] = useState<Partial<AreaResponse> | null>(null);

  useEffect(() => {
    const cookieValue = getAreaCookie();
    const parsed = parseAreaCookie(cookieValue);

    if (parsed) {
      setAreaFromCookie(cookieToArea(parsed));
    }
  }, []);

  return areaFromCookie;
}
